/**
 * Voucher rules that differ between the original screens and the new look.
 * Per look for now; see CLAUDE.md, "The new look". Do not add a third look
 * here, and do not change one look to match the other without that decision.
 *
 * - `identityConfirmationRequired`: the step 0 box "Yes, I am {name} and I am
 *   {first}'s {relationship}." is an identity check. The new look disables
 *   Continue until it is ticked; the old look is non-blocking. Consent itself
 *   is the "By continuing, I agree…" line, recorded by `handleConsent` before
 *   anything is collected, on both looks.
 * - `everyAnswerRequired`: the new look asks one question at a time and needs
 *   an answer to each ("Not sure" is offered on the soft questions, so nobody
 *   is trapped). The old look only gates on Q1 = Yes and accepts partial
 *   answers.
 */
export interface VoucherLookRules {
  identityConfirmationRequired: boolean;
  everyAnswerRequired: boolean;
}

export function voucherLookRules(newLook: boolean): VoucherLookRules {
  return {
    identityConfirmationRequired: newLook,
    everyAnswerRequired: newLook,
  };
}

/**
 * Is Continue on the questions step blocked? Q1 ("Do you personally know
 * {first}?") must be Yes on both looks. On a look that needs every answer, a
 * later question also needs its own answer before moving on.
 */
export function questionsContinueBlocked(
  rules: VoucherLookRules,
  answers: Readonly<Record<number, string>>,
  questionIndex: number,
): boolean {
  if (!rules.everyAnswerRequired || questionIndex === 0) return answers[0] !== "Yes";
  return !answers[questionIndex];
}

/** The questions without an answer, in order. */
export function unansweredQuestions(
  answers: Readonly<Record<number, string>>,
  questionCount: number,
): number[] {
  const missing: number[] = [];
  for (let i = 0; i < questionCount; i++) {
    if (!answers[i]) missing.push(i);
  }
  return missing;
}

/** Is Submit blocked by unanswered questions? Never on the old look. */
export function questionsSubmitBlocked(
  rules: VoucherLookRules,
  answers: Readonly<Record<number, string>>,
  questionCount: number,
): boolean {
  return rules.everyAnswerRequired && unansweredQuestions(answers, questionCount).length > 0;
}

/**
 * A No on Q1 opens the "It's okay if you don't know them" dialog, whose
 * primary action records `unable_to_confirm`. Same on both looks.
 */
export function answerOpensUnableToConfirm(questionIndex: number, option: string): boolean {
  return questionIndex === 0 && option === "No";
}
